import axios from 'axios'
import { fetchAuthSession } from 'aws-amplify/auth'
import { HttpRequest, HttpResponse } from '../../../data/protocols/http/http-client'
import { clearStorage } from '../../../presentation/utils/clear_storage'
import { clearLocalStorage, getLocalStorage } from '../../../presentation/utils/storage_helpers'
import { addLocalStorage } from '../../../infra/storage/localStorage'
import CryptoJS from 'crypto-js'
import { settings } from '../../../main/configs/settings'

// Constantes para segurança de frontend
const API_SECRET = process.env.REACT_APP_FRONTEND_SECRET

export class AxiosHttpClient {
  api: any

  constructor (private readonly baseUrl: string | undefined) {
    const api = axios
    api.interceptors.response.use(this.handleSuccess, this.handleError)
    this.api = api
  }

  private makeCrypt (message: string): string {
    const key = CryptoJS.enc.Hex.parse(settings.secret)
    const iv = CryptoJS.enc.Hex.parse(settings.secret_iv)
    const encrypted = CryptoJS.AES.encrypt(message, key, {
      iv,
      padding: CryptoJS.pad.ZeroPadding
    })

    return encrypted.toString()
  }

  private generateRequestFingerprint (): string {
    const highPrecisionTime = performance.now().toString()
    const randomValue = Math.random().toString(36).substring(2, 15)
    const browserInfo = navigator.userAgent
    return `${highPrecisionTime}-${randomValue}-${browserInfo}`
  }

  private async generateSecurityHeaders (path: string): Promise<{
    token: string,
    timestamp: string,
    signature: string,
    requestId: string
  }> {
    try {
      const timestamp = Date.now().toString()
      const requestId = this.generateRequestFingerprint()
      const tokenData = `${timestamp}:${path}:${requestId}`
      const encoder = new TextEncoder()
      const keyData = encoder.encode(API_SECRET)
      const messageData = encoder.encode(tokenData)
      const key = await window.crypto.subtle.importKey(
        'raw',
        keyData,
        { name: 'HMAC', hash: 'SHA-256' },
        false,
        ['sign']
      )
      const signatureBuffer = await window.crypto.subtle.sign(
        'HMAC',
        key,
        messageData
      )
      const token = Array.from(new Uint8Array(signatureBuffer))
        .map(b => b.toString(16).padStart(2, '0'))
        .join('')
      const signaturePayload = JSON.stringify({
        token,
        timestamp,
        requestId,
        path,
        screenWidth: window.screen.width,
        screenHeight: window.screen.height,
        timezoneOffset: new Date().getTimezoneOffset(),
        preciseTime: performance.now(),
        random: Math.random().toString(36).substring(2, 15)
      })

      const signature = this.makeCrypt(signaturePayload)

      return { token, timestamp, signature, requestId }
    } catch (error) {
      console.error('Erro ao gerar headers de segurança:', error)
      return { token: '', timestamp: '', signature: '', requestId: '' }
    }
  }

  async request ({ headers, method, url, data, params }: HttpRequest): Promise<HttpResponse> {
    let axiosResponse
    try {
      const authToken = await this.updateAmplifyToken()
      const path = url
      const { token, timestamp, signature, requestId } = await this.generateSecurityHeaders(path)
      const securityHeaders = {
        'X-FRONTEND-TOKEN': token,
        'X-REQUEST-TIMESTAMP': timestamp,
        'X-REQUEST-ID': requestId,
        'X-SECURITY-SIGNATURE': signature,
        ...authToken,
        ...headers
      }
      const processedData = data
      if (data && (['POST', 'PUT', 'PATCH'].includes(String(method)))) {
        const dataStr = typeof data === 'object' ? JSON.stringify(data) : String(data)
        const dataSignature = this.makeCrypt(`${timestamp}:${dataStr}:${requestId}`)
        securityHeaders['X-DATA-SIGNATURE'] = dataSignature
      }

      axiosResponse = await this.api.request({
        url: this.baseUrl + url,
        method,
        data: processedData,
        headers: securityHeaders,
        params
      })
    } catch (error: any) {
      axiosResponse = error.response
    }

    return {
      statusCode: axiosResponse.status,
      body: axiosResponse.data
    }
  }

  async requestWithouAuth ({ headers, method, url, data, params }: HttpRequest): Promise<HttpResponse> {
    let axiosResponse
    try {
      const path = url
      const { token, timestamp, signature, requestId } = await this.generateSecurityHeaders(path)
      axiosResponse = await this.api.request({
        url: this.baseUrl + url,
        method,
        data,
        headers: {
          'X-FRONTEND-TOKEN': token,
          'X-REQUEST-TIMESTAMP': timestamp,
          'X-REQUEST-ID': requestId,
          'X-SECURITY-SIGNATURE': signature,
          ...headers
        },
        params
      })
    } catch (error: any) {
      axiosResponse = error.response
    }

    return {
      statusCode: axiosResponse.status,
      body: axiosResponse.data
    }
  }

  updateAmplifyToken = async () => {
    try {
      const { tokens } = await fetchAuthSession()
      if (tokens?.idToken?.toString()) {
        return {
          Authorization: tokens.idToken.toString()
        }
      }
      return {}
    } catch (error) {
      console.error('Error fetching auth session:', error)
      return {}
    }
  }

  handleSuccess = (response: any) => {
    return response
  }

  handleError = (error: { response: { status: any, request: any } }) => {
    const { status } = error.response

    const saveSupport = getLocalStorage('support')
    const saveLanguage = getLocalStorage('gameLanguage')

    switch (status) {
      case 401:
        this.redirectTo(document, '/sair')
        break
      case 403:
        clearStorage()
        clearLocalStorage()
        addLocalStorage('support', saveSupport)
        addLocalStorage('gameLanguage', saveLanguage)
        this.redirectTo(document, '/403')
        break
      case 404:
        clearStorage()
        clearLocalStorage()
        addLocalStorage('support', saveSupport)
        addLocalStorage('gameLanguage', saveLanguage)
        this.redirectTo(document, '/404')
        break
    }
    return Promise.reject(error)
  }

  redirectTo = (document: { location: any }, path: any) => {
    document.location = path
  }
}
